/**
 * AccountDataTransfer.js
 * 
 * Ermöglicht das Übertragen von Daten von Account A zu Account B.
 * Kopiert alle World-Daten: Settings, Topics, DB, Inbox, Emails etc.
 * 
 * Verwendet Cloud Functions mit Admin-Privilegien für den Transfer.
 */

import React, { useState, useEffect, useRef } from 'react';
import { database, app, firestore } from '../firebaseConfig';
import { ref, get } from 'firebase/database';
import { doc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AccountDataTransfer.css';

// Firebase Functions initialisieren mit der korrekten App-Instanz (Europe West 1)
const functions = getFunctions(app, 'europe-west1');

// Erhöhtes Timeout für große Transfers (5 Minuten pro Kategorie)
const FUNCTION_TIMEOUT = 300000; // 5 Minuten in ms

// Generiere eindeutige Transfer-ID
const generateTransferId = () => {
  return `transfer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Daten-Kategorien die übertragen werden können
const DATA_CATEGORIES = [
  { 
    key: 'settings', 
    label: 'Settings', 
    description: 'AI Settings, Email Config, Footer, Cleanup Guidelines, Tags',
    icon: '⚙️',
    database: 'rtdb',
    paths: ['settings']
  },
  { 
    key: 'topics', 
    label: 'Email Topics', 
    description: 'Email templates and topics',
    icon: '📝',
    database: 'rtdb',
    paths: ['topics']
  },
  { 
    key: 'emailAccounts', 
    label: 'Email Accounts', 
    description: 'Connected email accounts (IMAP/SMTP)',
    icon: '📧',
    database: 'firestore',
    collection: 'world_users',
    subCollection: 'emailAccounts'
  },
  { 
    key: 'contacts', 
    label: 'Contacts Database', 
    description: 'All contacts/leads in the database',
    icon: '👥',
    database: 'firestore',
    collection: 'World',
    subCollection: 'db'
  },
  { 
    key: 'inbox', 
    label: 'Inbox Emails', 
    description: 'Received emails in Smart Inbox',
    icon: '📥',
    database: 'firestore',
    collection: 'world_inbox',
    subCollection: 'emails'
  },
  { 
    key: 'sent', 
    label: 'Sent Emails', 
    description: 'All sent emails',
    icon: '📤',
    database: 'firestore',
    collection: 'world_sent',
    subCollection: 'emails'
  },
  { 
    key: 'drafts', 
    label: 'Email Drafts', 
    description: 'Saved email drafts',
    icon: '📋',
    database: 'firestore',
    collection: 'world_drafts',
    subCollection: 'emails'
  },
  { 
    key: 'importNames', 
    label: 'Import Names', 
    description: 'Saved import name records',
    icon: '📁',
    database: 'firestore',
    collection: 'World',
    subCollection: 'import_names'
  },
  { 
    key: 'touchpoints', 
    label: 'Touchpoint Activity', 
    description: 'Lead touchpoint history',
    icon: '📊',
    database: 'firestore',
    collection: 'World',
    subCollection: 'touchpointActivity'
  }
];

function AccountDataTransfer() {
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceAccount, setSourceAccount] = useState(null);
  const [targetAccount, setTargetAccount] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(
    DATA_CATEGORIES.map(cat => cat.key) // Standardmäßig alle ausgewählt
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferProgress, setTransferProgress] = useState({ current: 0, total: 0, status: '' });
  const [transferLog, setTransferLog] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: Select Source, 2: Select Target, 3: Select Data, 4: Confirm, 5: Transfer
  const [dataCounts, setDataCounts] = useState({});
  const [currentTransferId, setCurrentTransferId] = useState(null);
  const [categoryProgress, setCategoryProgress] = useState({ current: 0, total: 0, percent: 0 });
  const unsubscribeRef = useRef(null);

  // Lade alle User beim Mount
  useEffect(() => {
    loadAllUsers();
  }, []);

  // Filter Users bei Suche
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(allUsers);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(
        allUsers.filter(user => 
          user.email?.toLowerCase().includes(term) ||
          user.name?.toLowerCase().includes(term) ||
          user.id?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, allUsers]);

  const loadAllUsers = async () => {
    setIsLoading(true);
    try {
      const usersRef = ref(database, 'Users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const users = [];
        const data = snapshot.val();
        
        for (const [id, userData] of Object.entries(data)) {
          users.push({
            id,
            email: userData.email || 'No Email',
            name: userData.name || userData.firstName || 'Unknown',
            isPremium: userData.isPremium || false,
            emailPlan: userData.emailPlan || 'standard',
            createdAt: userData.signupTimestamp || null
          });
        }
        
        // Sortiere nach E-Mail
        users.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setAllUsers(users);
        setFilteredUsers(users);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Zähle Daten für Source Account (via Cloud Function mit Admin-Rechten)
  const countSourceData = async (userId) => {
    if (!userId) return;
    
    try {
      addLog(`Counting data for ${userId}...`, 'info');
      
      const countAccountDataFn = httpsCallable(functions, 'countAccountData');
      const result = await countAccountDataFn({ userId });
      
      setDataCounts(result.data);
      addLog(`Data counts loaded successfully`, 'success');
    } catch (err) {
      console.error('Error counting data:', err);
      addLog(`Error counting data: ${err.message}`, 'error');
      
      // Fallback: Nur RTDB Counts (da diese keine Permission-Probleme haben)
      const counts = {};
      for (const cat of DATA_CATEGORIES.filter(c => c.database === 'rtdb')) {
        try {
          const dataRef = ref(database, `World/${userId}/${cat.paths[0]}`);
          const snapshot = await get(dataRef);
          counts[cat.key] = snapshot.exists() ? Object.keys(snapshot.val()).length : 0;
        } catch (e) {
          counts[cat.key] = '?';
        }
      }
      // Firestore counts als "?" markieren da wir keine Berechtigung haben
      for (const cat of DATA_CATEGORIES.filter(c => c.database === 'firestore')) {
        counts[cat.key] = '?';
      }
      setDataCounts(counts);
    }
  };

  // Source Account auswählen
  const handleSelectSource = async (user) => {
    setSourceAccount(user);
    setTargetAccount(null); // Reset target bei Source-Wechsel
    await countSourceData(user.id);
    setStep(2);
  };

  // Target Account auswählen
  const handleSelectTarget = (user) => {
    if (user.id === sourceAccount?.id) {
      setError('Source and Target account cannot be the same');
      return;
    }
    setTargetAccount(user);
    setStep(3);
  };

  // Kategorie-Auswahl togglen
  const toggleCategory = (key) => {
    setSelectedCategories(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  // Alle auswählen/abwählen
  const toggleAllCategories = () => {
    if (selectedCategories.length === DATA_CATEGORIES.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(DATA_CATEGORIES.map(c => c.key));
    }
  };

  // Log-Eintrag hinzufügen
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setTransferLog(prev => [...prev, { message, type, timestamp }]);
  };

  // Cleanup Firestore Listener beim Unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Haupttransfer-Funktion mit Echtzeit-Feedback
  const startTransfer = async () => {
    if (!sourceAccount || !targetAccount || selectedCategories.length === 0) {
      setError('Please complete all steps first');
      return;
    }

    setIsTransferring(true);
    setTransferLog([]);
    setError('');
    setSuccess('');
    setStep(5);
    setCategoryProgress({ current: 0, total: 0, percent: 0 });

    const sourceId = sourceAccount.id;
    const targetId = targetAccount.id;
    const totalSteps = selectedCategories.length;
    
    // Generiere Transfer-ID für Echtzeit-Tracking
    const transferId = generateTransferId();
    setCurrentTransferId(transferId);

    addLog(`Starting transfer from ${sourceAccount.email} to ${targetAccount.email}`, 'info');
    addLog(`Transfer ID: ${transferId}`, 'info');
    addLog(`Watching for real-time progress updates...`, 'info');

    // Starte Firestore Listener für Echtzeit-Updates
    const progressDocRef = doc(firestore, 'transfer_progress', transferId);
    
    unsubscribeRef.current = onSnapshot(progressDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        
        // Update Kategorie-Fortschritt
        if (data.categoryProgress !== undefined && data.categoryTotal) {
          setCategoryProgress({
            current: data.categoryProgress,
            total: data.categoryTotal,
            percent: data.categoryPercent || 0,
            category: data.currentCategory
          });
        }

        // Update Gesamt-Fortschritt
        if (data.currentCategoryIndex !== undefined) {
          setTransferProgress({
            current: data.currentCategoryIndex,
            total: data.totalCategories || totalSteps,
            status: data.currentCategory ? `Transferring ${data.currentCategory}...` : 'Processing...'
          });
        }

        // Neue Log-Einträge hinzufügen
        if (data.logs && Array.isArray(data.logs)) {
          setTransferLog(data.logs.map(log => ({
            message: log.message,
            type: log.type,
            timestamp: new Date(log.timestamp).toLocaleTimeString()
          })));
        }

        // Status-Updates
        if (data.status === 'completed') {
          setTransferProgress({ current: totalSteps, total: totalSteps, status: 'Complete!' });
          setSuccess(`Successfully transferred data from ${sourceAccount.email} to ${targetAccount.email}`);
          setIsTransferring(false);
        } else if (data.status === 'completed_with_errors') {
          setTransferProgress({ current: totalSteps, total: totalSteps, status: 'Complete with errors' });
          setError('Transfer completed but some categories failed. Check the log for details.');
          setIsTransferring(false);
        }
      }
    }, (err) => {
      console.error('Firestore listener error:', err);
    });

    // Cloud Function mit erhöhtem Timeout - ALLE Kategorien auf einmal
    const transferAccountDataFn = httpsCallable(functions, 'transferAccountData', {
      timeout: FUNCTION_TIMEOUT
    });

    try {
      setTransferProgress({ 
        current: 0, 
        total: totalSteps, 
        status: 'Starting transfer...' 
      });

      // Alle Kategorien in einem Call mit Transfer-ID für Echtzeit-Tracking
      const result = await transferAccountDataFn({
        sourceUserId: sourceId,
        targetUserId: targetId,
        categories: selectedCategories,
        transferId: transferId // Für Echtzeit-Progress
      });

      const { transferred, errors, success } = result.data;

      // Cloud Function ist fertig - Listener hat bereits die Logs aktualisiert
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setTransferProgress({ current: totalSteps, total: totalSteps, status: 'Complete!' });
      
      if (success) {
        setSuccess(`Successfully transferred data from ${sourceAccount.email} to ${targetAccount.email}`);
      } else {
        setError(`Transfer completed but some categories failed. Check the log for details.`);
      }

      // Cleanup: Lösche Progress-Dokument nach erfolgreichem Transfer
      try {
        await deleteDoc(progressDocRef);
      } catch (e) {
        console.log('Could not delete progress doc:', e);
      }

    } catch (err) {
      console.error('Transfer error:', err);
      
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      // Bei Timeout: Die Cloud Function läuft möglicherweise noch weiter!
      if (err.code === 'functions/deadline-exceeded') {
        setError('Client timeout - but the transfer may still be running in the background. Check the logs.');
        addLog(`⚠️ Client timeout reached. The server may still be processing.`, 'warning');
      } else {
        setError(`Transfer failed: ${err.message}`);
        addLog(`Transfer failed: ${err.message}`, 'error');
      }
      
      if (err.code) {
        addLog(`Error code: ${err.code}`, 'error');
      }
    } finally {
      setIsTransferring(false);
    }
  };

  // Reset zum Anfang
  const resetTransfer = () => {
    setStep(1);
    setSourceAccount(null);
    setTargetAccount(null);
    setSelectedCategories(DATA_CATEGORIES.map(c => c.key));
    setTransferLog([]);
    setTransferProgress({ current: 0, total: 0, status: '' });
    setError('');
    setSuccess('');
    setDataCounts({});
  };

  // Render User Card
  const renderUserCard = (user, onClick, isSelected = false, isDisabled = false) => (
    <div 
      key={user.id}
      className={`adt_user_card ${isSelected ? 'adt_user_card_selected' : ''} ${isDisabled ? 'adt_user_card_disabled' : ''}`}
      onClick={() => !isDisabled && onClick(user)}
    >
      <div className="adt_user_avatar">
        {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <div className="adt_user_info">
        <div className="adt_user_name">{user.name}</div>
        <div className="adt_user_email">{user.email}</div>
        <div className="adt_user_id">{user.id.substring(0, 12)}...</div>
      </div>
      <div className="adt_user_badges">
        {user.isPremium && <span className="adt_badge adt_badge_premium">PRO</span>}
        <span className={`adt_badge adt_badge_plan_${user.emailPlan}`}>
          {user.emailPlan?.toUpperCase() || 'STANDARD'}
        </span>
      </div>
      {isSelected && (
        <div className="adt_check_mark">✓</div>
      )}
      {isDisabled && (
        <div className="adt_disabled_overlay">Source Account</div>
      )}
    </div>
  );

  return (
    <div className="adt_container">
      <div className="adt_header">
        <h2 className="adt_title">
          <span className="adt_title_icon">🔄</span>
          Account Data Transfer
        </h2>
        <p className="adt_subtitle">
          Copy all World data from one account to another
        </p>
      </div>

      {/* Progress Steps */}
      <div className="adt_steps">
        <div className={`adt_step ${step >= 1 ? 'adt_step_active' : ''} ${step > 1 ? 'adt_step_done' : ''}`}>
          <div className="adt_step_number">1</div>
          <div className="adt_step_label">Source Account</div>
        </div>
        <div className="adt_step_line"></div>
        <div className={`adt_step ${step >= 2 ? 'adt_step_active' : ''} ${step > 2 ? 'adt_step_done' : ''}`}>
          <div className="adt_step_number">2</div>
          <div className="adt_step_label">Target Account</div>
        </div>
        <div className="adt_step_line"></div>
        <div className={`adt_step ${step >= 3 ? 'adt_step_active' : ''} ${step > 3 ? 'adt_step_done' : ''}`}>
          <div className="adt_step_number">3</div>
          <div className="adt_step_label">Select Data</div>
        </div>
        <div className="adt_step_line"></div>
        <div className={`adt_step ${step >= 4 ? 'adt_step_active' : ''} ${step > 4 ? 'adt_step_done' : ''}`}>
          <div className="adt_step_number">4</div>
          <div className="adt_step_label">Confirm</div>
        </div>
        <div className="adt_step_line"></div>
        <div className={`adt_step ${step >= 5 ? 'adt_step_active' : ''}`}>
          <div className="adt_step_number">5</div>
          <div className="adt_step_label">Transfer</div>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="adt_message adt_message_error">{error}</div>}
      {success && <div className="adt_message adt_message_success">{success}</div>}

      {/* Step 1 & 2: Select Accounts */}
      {(step === 1 || step === 2) && (
        <div className="adt_section">
          <div className="adt_section_header">
            <h3>{step === 1 ? 'Select Source Account' : 'Select Target Account'}</h3>
            <p>{step === 1 
              ? 'Choose the account to copy data FROM' 
              : 'Choose the account to copy data TO'
            }</p>
          </div>

          {/* Selected Account Preview */}
          {step === 2 && sourceAccount && (
            <div className="adt_selected_preview">
              <div className="adt_selected_label">Source Account:</div>
              <div className="adt_selected_account">
                <span className="adt_selected_email">{sourceAccount.email}</span>
                <button className="adt_change_btn" onClick={() => setStep(1)}>Change</button>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="adt_search_container">
            <input
              type="text"
              className="adt_search_input"
              placeholder="Search by email, name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="adt_search_icon">🔍</span>
          </div>

          {/* User List */}
          {isLoading ? (
            <div className="adt_loading">
              <div className="adt_spinner"></div>
              <span>Loading users...</span>
            </div>
          ) : (
            <div className="adt_user_list">
              {filteredUsers.map(user => renderUserCard(
                user,
                step === 1 ? handleSelectSource : handleSelectTarget,
                step === 1 ? sourceAccount?.id === user.id : targetAccount?.id === user.id,
                step === 2 && sourceAccount?.id === user.id
              ))}
              {filteredUsers.length === 0 && (
                <div className="adt_empty">No users found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Select Data Categories */}
      {step === 3 && (
        <div className="adt_section">
          <div className="adt_section_header">
            <h3>Select Data to Transfer</h3>
            <p>Choose which data categories to copy</p>
          </div>

          {/* Account Summary */}
          <div className="adt_transfer_summary">
            <div className="adt_summary_account">
              <div className="adt_summary_label">From:</div>
              <div className="adt_summary_email">{sourceAccount?.email}</div>
            </div>
            <div className="adt_summary_arrow">→</div>
            <div className="adt_summary_account">
              <div className="adt_summary_label">To:</div>
              <div className="adt_summary_email">{targetAccount?.email}</div>
            </div>
          </div>

          {/* Select All */}
          <div className="adt_select_all">
            <label className="adt_checkbox_label">
              <input
                type="checkbox"
                checked={selectedCategories.length === DATA_CATEGORIES.length}
                onChange={toggleAllCategories}
              />
              <span className="adt_checkbox_custom"></span>
              Select All
            </label>
          </div>

          {/* Categories Grid */}
          <div className="adt_categories_grid">
            {DATA_CATEGORIES.map(category => (
              <div 
                key={category.key}
                className={`adt_category_card ${selectedCategories.includes(category.key) ? 'adt_category_selected' : ''}`}
                onClick={() => toggleCategory(category.key)}
              >
                <div className="adt_category_checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.key)}
                    onChange={() => toggleCategory(category.key)}
                  />
                  <span className="adt_checkbox_custom"></span>
                </div>
                <div className="adt_category_icon">{category.icon}</div>
                <div className="adt_category_info">
                  <div className="adt_category_label">{category.label}</div>
                  <div className="adt_category_desc">{category.description}</div>
                  {dataCounts[category.key] !== undefined && (
                    <div className="adt_category_count">
                      {dataCounts[category.key]} items
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="adt_nav_buttons">
            <button className="adt_btn adt_btn_secondary" onClick={() => setStep(2)}>
              ← Back
            </button>
            <button 
              className="adt_btn adt_btn_primary"
              disabled={selectedCategories.length === 0}
              onClick={() => setStep(4)}
            >
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <div className="adt_section">
          <div className="adt_section_header">
            <h3>Confirm Transfer</h3>
            <p>Review and confirm the data transfer</p>
          </div>

          <div className="adt_confirm_box">
            <div className="adt_confirm_warning">
              <span className="adt_warning_icon">⚠️</span>
              <div className="adt_warning_text">
                <strong>Important:</strong> This will copy data from the source account to the target account.
                Existing data in the target account will be merged (not overwritten).
                This action cannot be undone easily.
              </div>
            </div>

            <div className="adt_confirm_details">
              <div className="adt_confirm_row">
                <span className="adt_confirm_label">Source Account:</span>
                <span className="adt_confirm_value">{sourceAccount?.email}</span>
              </div>
              <div className="adt_confirm_row">
                <span className="adt_confirm_label">Target Account:</span>
                <span className="adt_confirm_value">{targetAccount?.email}</span>
              </div>
              <div className="adt_confirm_row">
                <span className="adt_confirm_label">Data Categories:</span>
                <span className="adt_confirm_value">{selectedCategories.length} selected</span>
              </div>
              <div className="adt_confirm_categories">
                {selectedCategories.map(key => {
                  const cat = DATA_CATEGORIES.find(c => c.key === key);
                  return (
                    <span key={key} className="adt_confirm_category_badge">
                      {cat?.icon} {cat?.label}
                      {dataCounts[key] !== undefined && ` (${dataCounts[key]})`}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="adt_nav_buttons">
            <button className="adt_btn adt_btn_secondary" onClick={() => setStep(3)}>
              ← Back
            </button>
            <button 
              className="adt_btn adt_btn_danger"
              onClick={startTransfer}
            >
              🚀 Start Transfer
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Transfer in Progress / Complete */}
      {step === 5 && (
        <div className="adt_section">
          <div className="adt_section_header">
            <h3>{isTransferring ? 'Transfer in Progress...' : 'Transfer Complete'}</h3>
            {currentTransferId && isTransferring && (
              <p className="adt_transfer_id">ID: {currentTransferId}</p>
            )}
          </div>

          {/* Overall Progress Bar */}
          <div className="adt_progress_container">
            <div className="adt_progress_label">Overall Progress</div>
            <div className="adt_progress_bar">
              <div 
                className="adt_progress_fill"
                style={{ 
                  width: `${transferProgress.total ? (transferProgress.current / transferProgress.total) * 100 : 0}%` 
                }}
              ></div>
            </div>
            <div className="adt_progress_text">
              {transferProgress.status} ({transferProgress.current}/{transferProgress.total} categories)
            </div>
          </div>

          {/* Category Progress Bar (für große Datenmengen) */}
          {isTransferring && categoryProgress.total > 0 && (
            <div className="adt_progress_container adt_progress_secondary">
              <div className="adt_progress_label">
                📦 {categoryProgress.category}: {categoryProgress.current.toLocaleString()} / {categoryProgress.total.toLocaleString()} documents
              </div>
              <div className="adt_progress_bar adt_progress_bar_secondary">
                <div 
                  className="adt_progress_fill adt_progress_fill_secondary"
                  style={{ width: `${categoryProgress.percent}%` }}
                ></div>
              </div>
              <div className="adt_progress_text">
                {categoryProgress.percent}% complete
              </div>
            </div>
          )}

          {/* Transfer Log */}
          <div className="adt_log_container">
            <div className="adt_log_header">Transfer Log</div>
            <div className="adt_log_content">
              {transferLog.map((entry, index) => (
                <div key={index} className={`adt_log_entry adt_log_${entry.type}`}>
                  <span className="adt_log_time">[{entry.timestamp}]</span>
                  <span className="adt_log_message">{entry.message}</span>
                </div>
              ))}
              {transferLog.length === 0 && (
                <div className="adt_log_empty">Waiting for transfer to start...</div>
              )}
            </div>
          </div>

          {/* Complete Actions */}
          {!isTransferring && (
            <div className="adt_nav_buttons">
              <button className="adt_btn adt_btn_primary" onClick={resetTransfer}>
                Start New Transfer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountDataTransfer;

